import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import CountrySelect from "../components/CountrySelect";

/**
 * Post-onboarding contact capture screen.
 * Fired after the AI profile is generated. Asks for country + phone so we can
 * personalize nurture sequences and (Phase C) reach via WhatsApp/SMS.
 * Skippable — but heavily encouraged with two reasons + a soft skip link.
 */
export default function ContactCapture() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [countryCode, setCountryCode] = useState(null);
  const [country, setCountry] = useState(null);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (user && user.contact_captured_at) {
      // Already done — skip straight to editor
      navigate("/profile/edit", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async () => {
    if (!countryCode) {
      toast.error("Please select your country");
      return;
    }
    setSaving(true);
    try {
      // Auto-prefix dial code if phone doesn't start with +
      let phoneToSend = phone.trim();
      if (phoneToSend && !phoneToSend.startsWith("+")) {
        phoneToSend = `${country?.dial || ""}${phoneToSend}`;
      }
      await api.post("/profile/contact", {
        phone: phoneToSend || null,
        country: country?.name || null,
        country_code: countryCode,
      });
      toast.success("Contact saved");
      navigate("/profile/edit");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save contact");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      // Save just country if selected, skip phone
      if (countryCode) {
        await api.post("/profile/contact", {
          country: country?.name || null,
          country_code: countryCode,
        });
      } else {
        // Mark as captured anyway so they don't see this page again
        await api.post("/profile/contact", {});
      }
    } catch {}
    navigate("/profile/edit");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-12" data-testid="contact-capture-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Phone size={26} className="text-[#D4AF37] mx-auto mb-3" />
          <p className="text-[10px] text-[#D4AF37] tracking-[0.35em] uppercase mb-3">One last detail</p>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-3" data-testid="contact-capture-headline">
            Where are you based?
          </h1>
          <p className="text-sm text-[#A3A3A3] leading-relaxed">
            We use your country to surface relevant scouts and clubs. Your number is optional — but it lets us reach you when something matters (a scout enquiry, a trial offer).
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-bold text-[#666] uppercase tracking-[0.2em] block mb-2">Country</label>
            <CountrySelect
              value={countryCode}
              onChange={(code, c) => { setCountryCode(code); setCountry(c); }}
              placeholder="Search and select your country"
              testId="contact-country"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold text-[#666] uppercase tracking-[0.2em] block mb-2">Phone / WhatsApp <span className="text-[#444] normal-case">(optional)</span></label>
            <div className="flex">
              <div className="flex items-center justify-center bg-[#0A0A0A] border border-r-0 border-[#262626] px-3 text-sm text-[#888] min-w-[60px]" data-testid="contact-dialcode">
                {country?.dial || "+—"}
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^0-9+\s-]/g, ""))}
                placeholder="Phone number"
                className="flex-1 bg-[#0E0E0E] border border-[#262626] focus:border-[#D4AF37]/60 text-[#F5F5F5] text-sm px-3 py-3 rounded-r-sm outline-none transition-colors"
                data-testid="contact-phone"
              />
            </div>
            <p className="text-[10px] text-[#555] mt-1.5 leading-relaxed">We'll only reach out about real opportunities — never for marketing spam.</p>
          </div>

          <button
            onClick={handleSubmit}
            disabled={saving || !countryCode}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] font-black tracking-widest uppercase text-sm px-6 py-4 rounded-full hover:bg-[#E5C04A] active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="contact-save-btn"
          >
            {saving ? "SAVING..." : "CONTINUE"} <ArrowRight size={16} />
          </button>

          <button
            onClick={handleSkip}
            disabled={saving}
            className="w-full text-center text-[11px] text-[#666] hover:text-[#D4AF37] uppercase tracking-[0.2em] transition-colors py-2"
            data-testid="contact-skip-btn"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
